import {
  Component,
  ViewChild,
  ChangeDetectionStrategy,
  Renderer2,
  AfterViewChecked,
  OnDestroy,
  AfterViewInit,
  Type,
  ViewEncapsulation,
} from '@angular/core';
import { FieldTypeConfig, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FieldType, FormlyFieldProps } from '@ngx-formly/material/form-field';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { FocusMonitor } from '@angular/cdk/a11y';
import { NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormlySelectModule } from '@ngx-formly/core/select';

interface CheckboxProps extends FormlyFieldProps {
  indeterminate?: boolean;
  labelPosition?: 'before' | 'after';
}

export interface FormlyCheckboxFieldConfig extends FormlyFieldConfig<CheckboxProps> {
  type: 'mat-checkbox' | Type<MatCheckboxTypeComponent>;
}

@Component({
  selector: 'mat-checkbox-type',
  encapsulation  : ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone     : true,
  imports: [NgIf, MatIconModule, FormlyModule,  MatCheckboxModule,  ReactiveFormsModule, MatFormFieldModule, FormlySelectModule],
  template: `
    @if (formState.editMode) {
    <mat-checkbox
      [formControl]="formControl"
      [id]="id"
      [name]="field.name"
      [formlyAttributes]="field"
      [tabIndex]="props.tabindex"
      [indeterminate]="props.indeterminate && formControl.value == null"
      [color]="props.color"
      [labelPosition]="props.labelPosition"
    >
      {{ props.label }}
      <span
        *ngIf="props.required && props.hideRequiredMarker !== true"
        aria-hidden="true"
        class="mat-form-field-required-marker mat-mdc-form-field-required-marker"
        >*</span
      >
    </mat-checkbox>
    }@else if(field.formControl.value){
    <div class="my-6">
      <div class="max-w-full flex flex-row items-center space-y-1 text-gray-500  dark:text-gray-400 ">
        <mat-icon class="mr-2" [svgIcon]="'mat_solid:check_box'"></mat-icon>
        <div>{{field.props.label}}</div>
      </div>
    </div>
    }@else if(!field.formControl.value){
      <div class="my-6">
      <div class="max-w-full flex flex-row items-center space-y-1 text-gray-500  dark:text-gray-400 ">
        <mat-icon class="mr-2" [svgIcon]="'mat_solid:check_box_outline_blank'"></mat-icon>
        <div>{{field.props.label}}</div>
      </div>
    </div>
    }
  `,
})
export class MatCheckboxTypeComponent
  extends FieldType<FieldTypeConfig<CheckboxProps>>
  implements AfterViewInit, AfterViewChecked, OnDestroy
{
  @ViewChild(MatCheckbox, { static: false }) checkbox!: MatCheckbox;
  override defaultOptions = {
    props: {
      hideFieldUnderline: true,
      indeterminate: true,
      floatLabel: 'always' as const,
      hideLabel: true,
      color: 'accent' as const, // workaround for https://github.com/angular/components/issues/18465
    },
  };

  private _required!: boolean;
  constructor(private renderer: Renderer2, private focusMonitor: FocusMonitor) {
    super();
  }

  override onContainerClick(event: MouseEvent): void {
    this.checkbox.focus();
    super.onContainerClick(event);
  }

  ngAfterViewInit() {
    if (this.checkbox) {
      this.focusMonitor.monitor(this.checkbox._inputElement, true).subscribe((focusOrigin) => {
        this.field.focus = !!focusOrigin;
        this.stateChanges.next();
        if (focusOrigin) {
          this.props.focus && this.props.focus(this.field);
        } else {
          this.props.blur && this.props.blur(this.field);
        }
      });
    }
  }

  ngAfterViewChecked() {
    if (this.required !== this._required && this.checkbox && this.checkbox._inputElement) {
      this._required = this.required;
      const inputElement = this.checkbox._inputElement.nativeElement;
      if (this.required) {
        this.renderer.setAttribute(inputElement, 'required', 'required');
      } else {
        this.renderer.removeAttribute(inputElement, 'required');
      }
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.checkbox) {
      this.focusMonitor.stopMonitoring(this.checkbox._inputElement);
    }
  }
}
